import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Update, Send, Remove, Get } from '../../services/service.api';
import { toast } from 'react-toastify';
import { Edit2, Plus, Trash2, X, AlertTriangle, Calendar, RotateCcw, Search, ArrowDown, ArrowUp } from 'lucide-react';
import ConfirmModal from '../../components/component.confirmModal';
import FullScreenLoader from '../../components/FullScreenLoader';
import Pagination, { usePageSize } from '../../components/component.pagination';

function formatFCFA(n) { return `${Number(n || 0).toLocaleString('fr-FR')} FCFA`; }

function SaleModal({ onClose, onSave, t, token, activeCompany, initialData = null }) {
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [form, setForm] = useState(initialData ? {
    ...initialData,
    date: initialData.date ? initialData.date.split('T')[0] : new Date().toISOString().split('T')[0]
  } : {
    client: '',
    produit_id: '',
    produit_libelle: '',
    quantite: '',
    campagne_id: '',
    date: new Date().toISOString().split('T')[0]
  });
  
  const [selectedProduct, setSelectedProduct] = useState(null);
  const total = selectedProduct ? Number(form.quantite || 0) * Number(selectedProduct.prix_unitaire_vente || 0) : (initialData?.total || 0);

  useEffect(() => {
    setLoadingData(true);
    Promise.all([
      Get(`/products?_t=${Date.now()}`, token),
      Get(`/campaigns?_t=${Date.now()}`, token),
    ]).then(([prodRes, campRes]) => {
      if (Array.isArray(prodRes)) {
        setProducts(prodRes);
        if (initialData?.produit_id) {
          const prod = prodRes.find(p => p.id === initialData.produit_id);
          if (prod) setSelectedProduct(prod);
        }
      }
      if (Array.isArray(campRes)) setCampaigns(campRes);
    }).catch(() => {}).finally(() => setLoadingData(false));
  }, [token, activeCompany, initialData]);

  const handleProductChange = (e) => {
    const product = products.find(p => p.id === e.target.value);
    setSelectedProduct(product || null);
    setForm({ ...form, produit_id: e.target.value, produit_libelle: product?.libelle || '' });
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.produit_id || !form.quantite || !form.campagne_id) return toast.error('Produit, quantité et campagne obligatoires');

    
    if (selectedProduct) {
      let availableStock = Number(selectedProduct.quantite || 0);
      if (initialData && initialData.produit_id === form.produit_id) {
        availableStock += Number(initialData.quantite || 0);
      }
      if (Number(form.quantite) > availableStock) {
        return toast.error(`Stock insuffisant ! Disponible (restauré) : ${availableStock}`);
      }
    }
    
    setSaving(true);
    try {
      await onSave({ ...form, total });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{initialData ? 'Modifier la vente' : t.sales.add}</h2>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t.sales.client}</label>
              <input
                className="form-control"
                value={form.client}
                onChange={e => setForm({ ...form, client: e.target.value })}
                placeholder="Nom du client (optionnel)"
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t.sales.produit} <span className="required">*</span></label>
              {loadingData ? (
                <div className="skeleton" style={{ height: 38, borderRadius: 'var(--radius-sm)' }} />
              ) : (
                <select className="form-select" value={form.produit_id} onChange={handleProductChange} required>
                  <option value="">-- Sélectionner un produit --</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id} disabled={Number(p.quantite) === 0}>
                      {p.libelle} — Stock: {p.quantite} | {formatFCFA(p.prix_unitaire_vente)}/u
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{t.sales.campagne || 'Campagne associée (optionnel)'}</label>
              {loadingData ? (
                <div className="skeleton" style={{ height: 38, borderRadius: 'var(--radius-sm)' }} />
              ) : (
                <select
                  className="form-select"
                  value={form.campagne_id}
                  onChange={e => setForm({ ...form, campagne_id: e.target.value })}
                  required
                >
                  <option value="">-- Sélectionnez une campagne --</option>
                  {campaigns.map(c => (
                    <option key={c.id} value={c.id}>{c.libelle}</option>
                  ))}
                </select>
              )}
            </div>

            {selectedProduct && (
              <div style={{
                background: 'var(--primary-glow)', borderRadius: 'var(--radius-sm)',
                padding: '10px 14px', marginBottom: 12, fontSize: '0.875rem'
              }}>
                <span style={{ color: 'var(--primary)', fontWeight: 600 }}>
                  📦 Stock disponible: {selectedProduct.quantite} unités
                  &nbsp;| Prix: {formatFCFA(selectedProduct.prix_unitaire_vente)}/u
                </span>
              </div>
            )}

            {selectedProduct && Number(selectedProduct.quantite) === 0 && (
              <div style={{
                background: 'var(--danger-bg)', borderRadius: 'var(--radius-sm)',
                padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center'
              }}>
                <AlertTriangle size={16} style={{ color: 'var(--danger)' }} />
                <span style={{ color: 'var(--danger)', fontSize: '0.875rem' }}>Stock épuisé !</span>
              </div>
            )}

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t.sales.quantite} <span className="required">*</span></label>
                <input
                  type="number" min="1"
                  max={selectedProduct?.quantite || undefined}
                  className="form-control" required
                  value={form.quantite}
                  onChange={e => setForm({ ...form, quantite: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t.sales.date}</label>
                <input
                  type="date" className="form-control"
                  value={form.date}
                  onChange={e => setForm({ ...form, date: e.target.value })}
                />
              </div>
            </div>

            {/* Prix unitaire (lecture seule) */}
            {selectedProduct && (
              <div className="form-group">
                <label className="form-label">{t.sales.prixVente}</label>
                <input
                  className="form-control"
                  value={formatFCFA(selectedProduct.prix_unitaire_vente)}
                  readOnly
                  style={{ color: 'var(--text-muted)', cursor: 'not-allowed' }}
                />
              </div>
            )}

            {/* Total calculé automatiquement */}
            <div style={{
              background: 'var(--bg-input)', border: '2px solid var(--primary)',
              borderRadius: 'var(--radius-sm)', padding: '12px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{t.sales.total}</span>
              <strong style={{ color: 'var(--primary)', fontSize: '1.25rem' }} className="number-format">
                {formatFCFA(total)}
              </strong>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : t.common.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Sales() {
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editSale, setEditSale] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize('sales');
  const [campaigns, setCampaigns] = useState([]);
  const [filterCampagne, setFilterCampagne] = useState('');

  // Filters
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');

  const abortControllerRef = useRef(null);

  useEffect(() => {
    Get(`/campaigns?_t=${Date.now()}`, token)
      .then(r => Array.isArray(r) && setCampaigns(r))
      .catch(() => {});
  }, [token, activeCompany?.id]);

  // Back to page 1 whenever the result set changes
  useEffect(() => { setCurrentPage(1); }, [searchQuery, filterCampagne, filterDateDebut, filterDateFin, sortOrder, pageSize, activeCompany?.id]);

  const fetchSales = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    try {
      const companyId = activeCompany?.id || '';
      const headers = { authorization: "Bearer " + token, 'Content-Type': 'application/json' };
      if (companyId) headers['X-Company-Id'] = companyId;

      const params = new URLSearchParams();
      if (filterDateDebut) params.append('date_debut', filterDateDebut);
      if (filterDateFin) params.append('date_fin', filterDateFin);
      if (companyId) params.append('company_id', companyId);
      params.append('_t', Date.now());
      const qs = params.toString() ? `?${params.toString()}` : '';

      const API_URL = process.env.REACT_APP_API_URL || "https://savaninvests-backend.onrender.com";
      const response = await fetch(`${API_URL}/sales${qs}`, { headers, signal });
      const res = await response.json();

      if (!signal.aborted && Array.isArray(res)) {
        setSales(res);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // silently fail
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [token, filterDateDebut, filterDateFin, activeCompany?.id]);

  useEffect(() => {
    setSales([]); // Clear to prevent ghost data
    fetchSales();
  }, [fetchSales]);

  useEffect(() => {
    const handleSwitched = () => fetchSales();
    window.addEventListener('company-switched', handleSwitched);
    return () => window.removeEventListener('company-switched', handleSwitched);
  }, [fetchSales]);

  const handleSave = async (form) => {
    try {
      let res;
      if (form.id) {
        res = await Update(`/sales/${form.id}`, form, token);
      } else {
        res = await Send('/sales', form, true, token);
      }
      if (res?.error) { toast.error(res.error); return; }
      toast.success(form.id ? 'Vente modifiée ✓' : 'Vente enregistrée ✓');
      setShowModal(false);
      setEditSale(null);
      fetchSales();
    } catch { toast.error('Erreur'); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await Remove(`/sales/${deleteId}`, token);
      toast.success('Vente supprimée');
      setDeleteId(null);
      fetchSales();
    } catch { toast.error('Erreur lors de la suppression'); }
  };

  const resetFilters = () => {
    setFilterDateDebut('');
    setFilterDateFin('');
    setSearchQuery('');
    setFilterCampagne('');
  };

  const isFilterActive = filterDateDebut !== '' || filterDateFin !== '' || searchQuery !== '' || filterCampagne;

  const filteredSales = sales.filter(s => {
    if (filterCampagne && String(s.campagne_id) !== filterCampagne) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (s.produit_libelle && String(s.produit_libelle).toLowerCase().includes(q)) ||
      (s.client && String(s.client).toLowerCase().includes(q)) ||
      (s.campagne_libelle && String(s.campagne_libelle).toLowerCase().includes(q))
    );
  }).sort((a, b) => {
    const dateA = new Date(a.date || a.created_at || 0).getTime();
    const dateB = new Date(b.date || b.created_at || 0).getTime();
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  const totalFCFA = filteredSales.reduce((acc, s) => acc + Number(s.total || 0), 0);
    const paginated = filteredSales.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="page-content page-enter">
      {loading && <FullScreenLoader message="Chargement des ventes..." />}
      <div className="page-header">
        <div>
          <h1>{t.sales.title}</h1>
          <p>Total : <strong className="number-format" style={{ color: 'var(--success)' }}>{formatFCFA(totalFCFA)}</strong> ({filteredSales.length} ventes)</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={16} /> {t.sales.add}
        </button>
      </div>

      {/* Filter Bar */}
      <div className="dashboard-filter-bar mb-4">
        <div className="filter-group-wrapper">
          <div className="search-wrapper" style={{ minWidth: 200, maxWidth: "100%" }}>
            <Search size={14} />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Rechercher produit / client..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ fontSize: '0.85rem' }}
            />
          </div>
          <select
            className="form-select"
            style={{ width: 'auto', maxWidth: '100%' }}
            value={filterCampagne}
            onChange={e => setFilterCampagne(e.target.value)}
            aria-label="Filtrer par campagne"
          >
            <option value="">Toutes les campagnes</option>
            {campaigns.map(c => <option key={c.id} value={String(c.id)}>{c.libelle}</option>)}
          </select>
          <button 
            className="btn btn-secondary" 
            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '5px' }}
          >
            {sortOrder === 'desc' ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
            Date ({sortOrder === 'desc' ? 'Récents' : 'Anciens'})
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={14} style={{ color: 'var(--gold)' }} />
            <input
              type="date"
              className="filter-input-date"
              value={filterDateDebut}
              onChange={e => setFilterDateDebut(e.target.value)}
              title="Date début"
            />
            <span style={{ color: 'var(--text-muted)' }}>→</span>
            <input
              type="date"
              className="filter-input-date"
              value={filterDateFin}
              onChange={e => setFilterDateFin(e.target.value)}
              title="Date fin"
            />
          </div>

          {isFilterActive && (
            <button
              className="btn btn-outline btn-sm"
              style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
              onClick={resetFilters}
              title="Réinitialiser"
            >
              <RotateCcw size={12} />
              Reset
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card">
          {[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8 }} />)}
        </div>
      ) : filteredSales.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">💰</div>
          <h3>{t.sales.noSales}</h3>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t.sales.client}</th>
                <th>{t.sales.produit}</th>
                <th>Campagne</th>
                <th>{t.sales.quantite}</th>
                <th>{t.sales.prixVente}</th>
                <th>{t.sales.total}</th>
                <th>{t.sales.date}</th>
                <th>{t.common.actions}</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(s => (
                <tr key={s.id}>
                  <td>{s.client || '—'}</td>
                  <td><strong>{s.produit_libelle}</strong></td>
                  <td>
                    {s.campagne_libelle ? (
                      <span className="badge badge-gold" style={{ fontSize: '0.75rem' }}>
                        {s.campagne_libelle}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                  <td className="number-format">{s.quantite}</td>
                  <td className="number-format">{formatFCFA(s.prix_unitaire_vente)}</td>
                  <td className="number-format">
                    <strong style={{ color: 'var(--success)' }}>{formatFCFA(s.total)}</strong>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    {s.date ? new Date(s.date).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => { setEditSale(s); setShowModal(true); }}
                        title="Modifier"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => setDeleteId(s.id)}
                        title={t.common.delete}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} style={{ fontWeight: 700, color: 'var(--text-secondary)' }}>Total</td>
                <td className="number-format" colSpan={3} style={{ fontWeight: 800, color: 'var(--success)', fontSize: '1.05rem' }}>
                  {formatFCFA(totalFCFA)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && filteredSales.length > 0 && (
        <Pagination
          page={currentPage}
          pageSize={pageSize}
          total={filteredSales.length}
          label="ventes"
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      )}

      {showModal && (
        <SaleModal
          initialData={editSale}
          onClose={() => { setShowModal(false); setEditSale(null); }}
          onSave={handleSave}
          t={t}
          token={token}
          activeCompany={activeCompany}
        />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Supprimer cette vente"
        message="Êtes-vous sûr de vouloir supprimer cette vente ? Cette opération restituera le stock du produit."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
